import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  ApiFilters,
  Pagination,
  SortDirection,
  StatePageSettings,
} from '@core/core.types';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from '@shared/components/toast-container/toast.service';
import { catchError, of, switchMap, tap, throwError } from 'rxjs';
import { PricingPartnerService } from '../services/pricing-partner.service';
import { PricingPartner, PricingPartnerList } from '../types/pricing.types';
import {
  AddPricingPartnerBundleAction,
  ApprovePricingPartnerPriceRequestAction,
  DeletePricingPartnerPriceRequestAction,
  DeletePricingPartnerBundlePriceAction,
  GetPricingPartnerAction,
  LoadPricingPartnersListAction,
  UpdatePricingPartnerAction,
  UpdatePricingPartnerBundlePriceAction,
} from './pricing-partner.actions';

const PRICING_STATE_NAME = 'pricing';

export interface PricingPartnerStateModel {
  pricingPartners: { [id: string]: PricingPartner };
  pages?: {
    [key: string]: {
      items: string[];
    };
  };
  currentPageSettings: StatePageSettings<PricingPartnerFilters>;
  totalCount?: number;
}

@State<PricingPartnerStateModel>({
  name: PRICING_STATE_NAME,
  defaults: {
    pricingPartners: {},
    pages: {},
    currentPageSettings: {
      pageIndex: 0,
      sortSettings: {
        orderBy: 'Generated',
        sortDirection: SortDirection.desc,
        pageSize: 30,
      },
    },
  },
})
@Injectable()
export class PricingPartnerState {
  private pricingPartnerService = inject(PricingPartnerService);
  private toastService = inject(ToastService);
  private translateService = inject(TranslateService);

  @Selector()
  static getPricingPartnerById(state: PricingPartnerStateModel) {
    return (pricingPartnerId: string) =>
      state.pricingPartners[pricingPartnerId];
  }

  @Selector()
  static getPricingByIds(state: PricingPartnerStateModel) {
    return (pricingPartnerIds: string[]) =>
      pricingPartnerIds
        ?.map((pricingPartnerId) => state.pricingPartners[pricingPartnerId])
        .filter((pricingPartner) => !!pricingPartner);
  }

  @Selector([PricingPartnerState, PricingPartnerState.getCurrentPageIds])
  static getCurrentPage(state: PricingPartnerStateModel, ids: string[]) {
    return ids
      .map((id) => state.pricingPartners[id])
      .filter((pricingPartner) => !!pricingPartner);
  }

  @Selector()
  static getCurrentPageIds(state: PricingPartnerStateModel) {
    const {
      currentPageSettings: { pageIndex },
      pages,
    } = state;

    if (Object.keys(pages).length === 0 || pageIndex < 0) {
      return [];
    }
    return state.pages[pageIndex].items.slice();
  }

  @Selector()
  static getPagination({
    currentPageSettings,
    totalCount,
    pages,
  }: PricingPartnerStateModel): Pagination<PricingPartnerFilters> {
    return {
      currentPageSettings: {
        ...currentPageSettings,
      },
      totalCount,
      pagesSize: Math.ceil(
        totalCount / currentPageSettings.sortSettings.pageSize
      ),
      collectionSize: currentPageSettings.sortSettings.pageSize,
    };
  }

  @Action(LoadPricingPartnersListAction)
  LoadPricingPartnersListAction(
    { patchState, getState }: StateContext<PricingPartnerStateModel>,
    { filters, pageToLoad = 1 }: LoadPricingPartnersListAction
  ) {
    return this.pricingPartnerService
      .getPricingPartnersList({ ...filters, page: pageToLoad })
      .pipe(
        tap((pricingPartners: PricingPartnerList) => {
          patchState({
            pricingPartners: {
              ...getState().pricingPartners,
              ...pricingPartners.data.reduce((acc, pricingPartner) => {
                acc[pricingPartner.id] = pricingPartner;
                return acc;
              }, {}),
            },
            totalCount: pricingPartners.totalCount,
            pages: {
              ...getState().pages,
              [pageToLoad - 1]: {
                items: pricingPartners.data.map(
                  (pricingPartner: PricingPartner) => pricingPartner.id
                ),
              },
            },
            currentPageSettings: {
              ...getState().currentPageSettings,
              pageSize: filters.limit,
              pageIndex: pageToLoad - 1,
              sortSettings: {
                ...getState().currentPageSettings.sortSettings,
                pageSize: filters.limit,
              },
            },
          });
        }),
        catchError((error: HttpErrorResponse) => {
          if (error.status === HttpStatusCode.Forbidden) {
            this.toastService.danger(
              this.translateService.instant(
                'pricing.errors.missingAccess.message'
              ),
              this.translateService.instant(
                'pricing.errors.missingAccess.title'
              ),
              0
            );
          } else {
            this.toastService.danger(error.error.message, error.error.error, 0);
          }

          patchState({
            pricingPartners: {},
            pages: {},
          });

          return throwError(() => error);
        })
      );
  }

  @Action(GetPricingPartnerAction)
  GetPricingPartnerAction(
    { dispatch, getState, patchState }: StateContext<PricingPartnerStateModel>,
    { id }: GetPricingPartnerAction
  ) {
    return this.pricingPartnerService.getPricingPartner(id).pipe(
      tap((pricingPartner: PricingPartner) => {
        patchState({
          pricingPartners: {
            ...getState().pricingPartners,
            [pricingPartner.id]: pricingPartner,
          },
        });
      }),
      catchError((error: HttpErrorResponse) => {
        this.toastService.danger(error.error?.message, error.error?.error, 0);
        return of(null);
      })
    );
  }

  @Action(UpdatePricingPartnerAction)
  UpdatePricingPartnerAction(
    { dispatch }: StateContext<PricingPartnerStateModel>,
    { partnerId, body }: UpdatePricingPartnerAction
  ) {
    return this.pricingPartnerService
      .updatePricingPartner(partnerId, body)
      .pipe(
        switchMap(() => dispatch(new GetPricingPartnerAction(partnerId))),
        catchError((error: HttpErrorResponse) => {
          this.toastService.danger(error.error?.message, error.error?.error, 0);
          return of(null);
        })
      );
  }

  @Action(AddPricingPartnerBundleAction)
  AddPricingPartnerBundleAction(
    { dispatch }: StateContext<PricingPartnerStateModel>,
    { partnerId, bundleId, price, validFrom }: AddPricingPartnerBundleAction
  ) {
    return this.pricingPartnerService
      .addBundleToPricingPartner(partnerId, bundleId, price, validFrom)
      .pipe(
        switchMap(() => dispatch(new GetPricingPartnerAction(partnerId))),
        catchError((error: HttpErrorResponse) => {
          this.toastService.danger(error.error?.message, error.error?.error, 0);
          return of(null);
        })
      );
  }

  @Action(UpdatePricingPartnerBundlePriceAction)
  UpdatePricingPartnerBundlePriceAction(
    { dispatch }: StateContext<PricingPartnerStateModel>,
    { partnerId, pivotId, price }: UpdatePricingPartnerBundlePriceAction
  ) {
    return this.pricingPartnerService
      .updatePricingPartnerBundlePrice(partnerId, pivotId, price)
      .pipe(
        switchMap(() => dispatch(new GetPricingPartnerAction(partnerId))),
        catchError((error: HttpErrorResponse) => {
          this.toastService.danger(error.error?.message, error.error?.error, 0);
          return of(null);
        })
      );
  }

  @Action(DeletePricingPartnerBundlePriceAction)
  DeletePricingPartnerBundlePriceAction(
    { dispatch }: StateContext<PricingPartnerStateModel>,
    { partnerId, pivotId }: DeletePricingPartnerBundlePriceAction
  ) {
    return this.pricingPartnerService
      .deletePricingPartnerBundlePrice(partnerId, pivotId)
      .pipe(
        switchMap(() => dispatch(new GetPricingPartnerAction(partnerId))),
        catchError((error: HttpErrorResponse) => {
          this.toastService.danger(error.error?.message, error.error?.error, 0);

          return of(null);
        })
      );
  }

  @Action(ApprovePricingPartnerPriceRequestAction)
  ApprovePricingPartnerPriceRequestAction(
    { dispatch }: StateContext<PricingPartnerStateModel>,
    { partnerId, requestId }: ApprovePricingPartnerPriceRequestAction
  ) {
    return this.pricingPartnerService
      .approvePricingPartnerPriceRequest(partnerId, requestId)
      .pipe(
        switchMap(() => dispatch(new GetPricingPartnerAction(partnerId))),
        catchError((error: HttpErrorResponse) => {
          this.toastService.danger(error.error?.message, error.error?.error, 0);
          return of(null);
        })
      );
  }

  @Action(DeletePricingPartnerPriceRequestAction)
  DeletePricingPartnerPriceRequestAction(
    { dispatch }: StateContext<PricingPartnerStateModel>,
    { partnerId, requestId }: DeletePricingPartnerPriceRequestAction
  ) {
    return this.pricingPartnerService
      .deletePricingPartnerPriceRequest(partnerId, requestId)
      .pipe(
        switchMap(() => dispatch(new GetPricingPartnerAction(partnerId))),
        catchError((error: HttpErrorResponse) => {
          this.toastService.danger(error.error?.message, error.error?.error, 0);
          return of(null);
        })
      );
  }
}

export interface PricingPartnerFilters extends ApiFilters {
  ids?: string[];
  name?: string;
  page?: number;
  limit?: number;
  notes?: string;
  currency?: string;
  partnerIds?: string[];
  countries?: string[];
  hasPriceRequests?: number;
  hasPendingRequests?: number;
  active?: boolean;
  paymentTypes?: string[];
}
